/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useState, useEffect, useRef, useCallback } from 'react';

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams, useLocation } from 'react-router-dom';

import Layout from '../../containers/Layout';
import Sidebar from '../../components/AddOrEditPage/Sidebar';
import Loan from '../../components/AddOrEditPage/AddOrEditLoan/Sections/Loan';
import Borrowers from '../../components/AddOrEditPage/AddOrEditLoan/Sections/Borrowers';
import Property from '../../components/AddOrEditPage/AddOrEditLoan/Sections/Property';
import Insurances from '../../components/AddOrEditPage/AddOrEditLoan/Sections/Insurances';
import Insurance from '../../components/AddOrEditPage/AddOrEditLoan/Sections/Insurance';
import Payments from '../../components/AddOrEditPage/AddOrEditLoan/Sections/Payments';
import Schedule from '../../components/AddOrEditPage/AddOrEditLoan/Sections/Schedule';
import Invoice from '../../components/AddOrEditPage/AddOrEditLoan/Sections/Invoice';
import Payment from '../../components/AddOrEditPage/AddOrEditLoan/Sections/Payment';
import Impounds from '../../components/AddOrEditPage/AddOrEditLoan/Sections/Impounds';
// import LienHolder from '../../components/AddOrEditPage/AddOrEditLoan/Sections/LienHolder';
import Actions from '../../components/AddOrEditPage/AddOrEditLoan/Sections/Actions';
import Settings from '../../components/AddOrEditPage/AddOrEditLoan/Sections/Settings';
import Documents from '../../components/AddOrEditPage/AddOrEditLoan/Sections/Documents';
import Document from '../../components/AddOrEditPage/AddOrEditLoan/Sections/Document';
import Participation from '../../components/AddOrEditPage/AddOrEditLoan/Sections/Participation';
import AddOrEditParticiaption from '../../components/AddOrEditPage/AddOrEditLoan/Sections/AddOrEditParticipation';
import Bills from '../../components/AddOrEditPage/AddOrEditLoan/Sections/Bills';
import Bill from '../../components/AddOrEditPage/AddOrEditLoan/Sections/Bill';
import BankAccount from '../../components/AddOrEditPage/BankAccount';
import Onboarding from '../../components/AddOrEditPage/AddOrEditLoan/Sections/Onboarding';
import BillPayments from '../../components/AddOrEditPage/AddOrEditLoan/Sections/BillPayments';
import BillPayment from '../../components/AddOrEditPage/AddOrEditLoan/Sections/BillPayment';
import AchSchedule from '../../components/AddOrEditPage/AddOrEditLoan/Sections/AchSchedule';
import Details from '../../components/AddOrEditPage/AddOrEditLoan/Sections/Details';
import BorrowerProperty from '../../components/AddOrEditPage/AddOrEditLoan/Sections/BorrowerProperty';
import BorrowerInsurances from '../../components/AddOrEditPage/AddOrEditLoan/Sections/BorrowerInsurances';
import LenderHistory from '../../components/AddOrEditPage/AddOrEditLoan/Sections/LenderHistory';
import Taxes from '../../components/AddOrEditPage/AddOrEditLoan/Sections/Taxes';
import Demands from '../../components/AddOrEditPage/AddOrEditLoan/Sections/Demands';
import Demand from '../../components/AddOrEditPage/AddOrEditLoan/Sections/Demand';
import Claims from '../../components/AddOrEditPage/AddOrEditLoan/Sections/Claims';
import Claim from '../../components/AddOrEditPage/AddOrEditLoan/Sections/Claim';
import ImpoundAnalysis from '../../components/AddOrEditPage/AddOrEditLoan/Sections/ImpoundAnalysis';
import ImpoundAnalyses from '../../components/AddOrEditPage/AddOrEditLoan/Sections/ImpoundAnalyses';
import Conversations from '../../components/AddOrEditPage/AddOrEditLoan/Sections/Conversations';
import Reports from '../../components/AddOrEditPage/AddOrEditLoan/Sections/Reports';

import LoanService from '../../services/LoanService';
import useAddQueryParam from '../../hooks/useAddQueryParams';
import { useServicerStore, useUiStore } from '../../store';
import useSections from './hooks/useSections';
import useResizeObserver from '../../hooks/useResizeObserver';
import useLoanWithIncludes from './hooks/useLoanWithIncludes';

function formatPageTitle(loanSummary, loan, isLender) {
  if (!loanSummary) {
    return '';
  }

  const number =
    loan?._lender?.pref_use_lender_loan_numbers && isLender
      ? loan.loan_number
      : loanSummary.pixel_loan_number;

  let title = `Loan: ${number}`;

  if (loanSummary._borrower_primary) {
    const borrower = loanSummary._borrower_primary;
    const name =
      borrower?.TIN_type === 'EIN'
        ? borrower?.entity_name
        : `${borrower?.first_name || ''} ${borrower?.last_name || ''}`;
    if (name) {
      title += ` | ${name}`;
    } else if (borrower?.name) {
      title += ` | ${borrower?.name}`;
    }
  }

  if (loanSummary._property) {
    const property = loanSummary._property;
    const address = [
      property.address_line_1,
      property.address_line_2,
      property.city,
      property.state,
      property.zip,
    ]
      .filter(Boolean)
      .join(', ');

    if (address) {
      title += ` | ${address}`;
    }
  }

  return title;
}

export default function AddOrEditLoanPage({ isEdit }) {
  const [borrowers, setBorrowers] = useState([]);

  const headingRef = useRef();
  const { height } = useResizeObserver(headingRef);

  const setLoanHeaderHeight = useUiStore((state) => state.setLoanHeaderHeight);
  useEffect(() => {
    setLoanHeaderHeight(height);
  }, [height, setLoanHeaderHeight]);

  const { id } = useParams();
  const location = useLocation();

  const queryClient = useQueryClient();
  const sections = useSections(queryClient);

  const { addQueryParam, currentQueryParam } = useAddQueryParam(
    'tab',
    location.pathname
  );

  const userRole = useServicerStore((state) => state.userRole);
  const isLender = userRole === 'lender';
  const isBorrower = userRole === 'borrower';

  const { loan, refetch, isFetching } = useLoanWithIncludes(id);

  const { data: loanSummary, refetch: refetchLoanSummary } = useQuery({
    queryKey: ['loanSummary', id],
    queryFn: () => LoanService.getLoanSummary(id),
    enabled: !!id,
  });

  const refetchLoan = useCallback(() => {
    return Promise.allSettled([refetch(), refetchLoanSummary()]);
  }, [refetch, refetchLoanSummary]);

  const switchToNextSection = useCallback(() => {
    const sectionIndex = sections.findIndex(
      (section) => section.title === currentQueryParam
    );

    if (sectionIndex < sections.length - 1) {
      addQueryParam('tab', sections[sectionIndex + 1].title);
    }
  }, [addQueryParam, currentQueryParam, sections]);

  // If borrowers already exist, set them
  useEffect(() => {
    if (loan?.borrowers) {
      setBorrowers(loan.borrowers);
    }
  }, [loan]);

  return (
    <Layout>
      <div className="w-full max-w-9xl mx-auto relative">
        <div className="sm:flex sm:justify-between sm:items-center mb-5 h-full">
          <div className="mb-4 sm:mb-0 w-screen md:w-full h-full relative">
            <div className="md:min-h-16 relative z-30" ref={headingRef}>
              <h1 className="px-4 sm:px-6 text-2xl md:text-3xl text-slate-800 dark:text-slate-100 font-bold bg-slate-100 dark:bg-slate-900 pt-6 z-20 after:absolute after:-top-1 after:h-2 after:w-full after:left-0 after:bg-slate-100 after:dark:bg-slate-900">
                {id ? formatPageTitle(loanSummary, loan, isLender) : 'New Loan'}
              </h1>
            </div>
            <div className="px-4 sm:px-6 flex md:flex-row flex-col">
              <div className="md:sticky md:top-[128px] md:h-[200px]">
                <Sidebar
                  isEdit={!!id}
                  sections={sections}
                  url={`/loans/${id}`}
                />
              </div>
              <div className="md:ml-6 2xl:mx-6 md:pt-8 flex-1 min-w-0">
                {!isBorrower && (
                  <div
                    style={{
                      display:
                        currentQueryParam === 'Loan' && !isBorrower
                          ? 'block'
                          : 'none',
                    }}
                  >
                    <Loan
                      isVisible={currentQueryParam === 'Loan'}
                      isEdit={isEdit}
                      switchToNextSection={switchToNextSection}
                      loan={loan}
                      refetch={refetchLoan}
                      readOnly={isLender}
                    />
                  </div>
                )}
                {currentQueryParam === 'Details' && id && (
                  <div>
                    <Details loan={loan} />
                  </div>
                )}
                {!isBorrower && (
                  <div
                    className="h-full"
                    style={{
                      display:
                        currentQueryParam === 'Borrowers' && id
                          ? 'block'
                          : 'none',
                    }}
                  >
                    <Borrowers
                      isVisible={currentQueryParam === 'Borrowers' && id}
                      setBorrowers={setBorrowers}
                      borrowers={borrowers}
                      loanId={id}
                      loan={loan}
                      switchToNextSection={switchToNextSection}
                      hasBorrowers={!!loan?.borrowers?.length}
                      readOnly={isLender}
                      refetchLoan={refetchLoan}
                    />
                  </div>
                )}
                {!isBorrower && (
                  <div
                    style={{
                      display:
                        currentQueryParam === 'Property' && id
                          ? 'block'
                          : 'none',
                    }}
                  >
                    <Property
                      isVisible={currentQueryParam === 'Property' && id}
                      loanId={id}
                      loan={loan}
                      refetchLoan={refetchLoan}
                      switchToNextSection={switchToNextSection}
                      readOnly={isLender}
                    />
                  </div>
                )}
                {!isBorrower && currentQueryParam === 'Taxes' && id && (
                  <div>
                    <Taxes loanId={id} loan={loan} />
                  </div>
                )}
                {isBorrower && currentQueryParam === 'Property' && id ? (
                  <div>
                    <BorrowerProperty loan={loan} />
                  </div>
                ) : null}
                <div
                  style={{
                    display:
                      currentQueryParam === 'Insurances' && id
                        ? 'block'
                        : 'none',
                  }}
                >
                  <Insurances loanId={id} readOnly={isLender} />
                </div>
                {currentQueryParam === 'Insurance' && id && (
                  <div>
                    {isBorrower ? (
                      <BorrowerInsurances
                        loanId={id}
                        loan={loan}
                        refetchLoan={refetchLoan}
                        readOnly={isLender}
                      />
                    ) : (
                      <Insurance
                        loanId={id}
                        loan={loan}
                        refetchLoan={refetchLoan}
                        readOnly={isLender}
                      />
                    )}
                  </div>
                )}
                {currentQueryParam === 'Claims' && id && (
                  <div>
                    <Claims loanId={id} />
                  </div>
                )}
                {currentQueryParam === 'Claim' && id && (
                  <div>
                    <Claim loanId={id} />
                  </div>
                )}
                {(currentQueryParam === 'Impounds' ||
                  currentQueryParam === 'Initial') &&
                  id && (
                    <div>
                      <Impounds
                        isVisible={currentQueryParam === 'Impounds' && id}
                        loanId={id}
                        loan={loan}
                        refetchLoan={refetchLoan}
                      />
                    </div>
                  )}
                {(currentQueryParam === 'Start Analysis' ||
                  currentQueryParam === 'Impound Analysis') &&
                  id && (
                    <div>
                      <ImpoundAnalysis loanId={id} loan={loan} />
                    </div>
                  )}
                {currentQueryParam === 'Analysis' && id && (
                  <div>
                    <ImpoundAnalyses loanId={id} loan={loan} />
                  </div>
                )}
                <div
                  style={{
                    display:
                      currentQueryParam === 'Payments' && id ? 'block' : 'none',
                  }}
                >
                  <Payments
                    isVisible={currentQueryParam === 'Payments' && id}
                    loan={loan}
                    loanId={id}
                    refetchLoan={refetchLoan}
                    isFetching={isFetching}
                    readOnly={isLender}
                    isLender={isLender}
                  />
                </div>
                {currentQueryParam === 'Bill Payments' && id && (
                  <div>
                    <BillPayments loanId={id} isFetching={isFetching} />
                  </div>
                )}
                {currentQueryParam === 'Bill Payment' && id && (
                  <div>
                    <BillPayment loanId={id} />
                  </div>
                )}
                {currentQueryParam === 'Bills' && id && (
                  <div>
                    <Bills
                      loan={loan}
                      loanId={id}
                      isFetching={isFetching}
                      refetchLoan={refetchLoan}
                      isLender={isLender}
                    />
                  </div>
                )}
                {currentQueryParam === 'Bank Account' && id && (
                  <div>
                    <BankAccount
                      loanId={id}
                      loan={loan}
                      isFetching={isFetching}
                      refetch={refetchLoan}
                    />
                  </div>
                )}
                {currentQueryParam === 'ACH Settings' && id && (
                  <div>
                    <AchSchedule
                      loanId={id}
                      loan={loan}
                      refetchLoan={refetchLoan}
                      readOnly={isLender}
                    />
                  </div>
                )}
                {currentQueryParam === 'Bill' && id && (
                  <div>
                    <Bill
                      loan={loan}
                      loanId={id}
                      isFetching={isFetching}
                      refetchLoan={refetchLoan}
                      isLender={isLender}
                    />
                  </div>
                )}
                {currentQueryParam === 'Payment' && id && (
                  <div>
                    <Payment
                      loanId={id}
                      refetchLoan={refetchLoan}
                      loan={loan}
                    />
                  </div>
                )}
                {currentQueryParam === 'Schedule' && id && (
                  <div>
                    <Schedule
                      loan={loan}
                      loanId={id}
                      isFetching={isFetching}
                      readOnly={isLender}
                    />
                  </div>
                )}
                {currentQueryParam === 'Invoice' && id && (
                  <div>
                    <Invoice
                      loanId={id}
                      readOnly={isLender || isBorrower}
                      loan={loan}
                    />
                  </div>
                )}
                {/* {currentQueryParam === 'Lien Holder' && id && (
                  <div>
                    <LienHolder
                      loanId={id}
                      loan={loan}
                      refetchLoan={refetchLoan}
                      readOnly={isLender}
                    />
                  </div>
                )} */}
                {currentQueryParam === 'Actions' && id && (
                  <div>
                    <Actions
                      loanId={id}
                      loan={loan}
                      refetchLoan={refetchLoan}
                    />
                  </div>
                )}
                {currentQueryParam === 'Demands' && id && (
                  <div>
                    <Demands loanId={id} />
                  </div>
                )}
                {currentQueryParam === 'Demand' && id && (
                  <div>
                    <Demand loanId={id} />
                  </div>
                )}
                {currentQueryParam === 'Lender History' &&
                  id &&
                  !isBorrower && (
                    <div>
                      <LenderHistory
                        loan={loan}
                        loanId={id}
                        refetch={refetchLoan}
                      />
                    </div>
                  )}
                {currentQueryParam === 'Settings' && id && (
                  <div>
                    <Settings
                      loan={loan}
                      isVisible
                      loanId={id}
                      isLender={isLender}
                    />
                  </div>
                )}
                {currentQueryParam === 'Conversations' && id && (
                  <div>
                    <Conversations
                      loanId={id}
                      borrowers={borrowers}
                      isLender={isLender}
                    />
                  </div>
                )}
                {currentQueryParam === 'Documents' && id && (
                  <div>
                    <Documents loanId={id} readOnly={isLender} />
                  </div>
                )}
                {currentQueryParam === 'Document' && id && !isLender && (
                  <div>
                    <Document loanId={id} />
                  </div>
                )}
                {currentQueryParam === 'Participation' && id && (
                  <Participation loanId={id} loan={loan} readOnly={isLender} />
                )}
                {currentQueryParam === 'Reports' && id && isLender && (
                  <Reports loanId={id} />
                )}
                {currentQueryParam === 'addOrEditParticipation' && id && (
                  <AddOrEditParticiaption
                    loanId={id}
                    loan={loan}
                    readOnly={isLender}
                    refetchLoan={refetchLoan}
                  />
                )}
                {currentQueryParam === 'Onboarding' && id && (
                  <div>
                    <Onboarding loanId={id} />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
